import { useState, useCallback, useEffect } from 'react';
import { useApi, useToaster } from '../../../../utils/hooks';
import { ToastStyle } from '../../../../utils/hooks/useToaster';
import {
  IClubMemberPaymentDto,
  IClubMemberPaymentAddRequest,
  IClubMemberPaymentResult,
  IAnnualInvoiceDto,
  ISystemCodeByTypeDto
} from '../../../../utils/constants/interfaces';
import { SortDirection, PaymentStatus } from '../../../../utils/constants/enums';
import { Helpers } from '../../../../utils/helpers';
import { getCurrentBillingYear } from '../../../../utils/helpers/billingYear';

type PaymentFormMode = 'disabled' | 'add' | 'edit';

interface UsePaymentsProps {
  clubMemberId: string;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
}

export const usePayments = ({ clubMemberId, fieldErrors, setFieldErrors }: UsePaymentsProps) => {
  const api = useApi();
  const toaster = useToaster();

  // State for payments
  const [payments, setPayments] = useState<IClubMemberPaymentDto[]>([]);
  const [loadingPayments, setLoadingPayments] = useState<boolean>(false);
  const [savingPayments, setSavingPayments] = useState<boolean>(false);
  const [paymentFormMode, setPaymentFormMode] = useState<PaymentFormMode>('disabled');
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [selectedPaymentRow, setSelectedPaymentRow] = useState<number | null>(null);

  // Payment method options state
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<ISystemCodeByTypeDto[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState<boolean>(false);

  // Payment filtering and sorting
  const [paymentYearFilter, setPaymentYearFilter] = useState<string>('All');
  const [paymentSortField, setPaymentSortField] = useState<string>('paymentDateUtc');
  const [paymentSortDirection, setPaymentSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );

  const [paymentFormData, setPaymentFormData] = useState<{
    paymentId: string;
    paymentDateUtc: string;
    billingYear: string;
    paymentMethodId: string;
    amount: string;
    notes: string;
  }>({
    paymentId: '',
    paymentDateUtc: Helpers.toDateString(new Date()),
    billingYear: getCurrentBillingYear().toString(),
    paymentMethodId: '',
    amount: '',
    notes: ''
  });

  // Load payment method options on component mount
  useEffect(() => {
    const loadPaymentMethods = async () => {
      setLoadingPaymentMethods(true);
      try {
        const response = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/PAYMENT_TYPE'
        );
        if (response?.status?.statusCode === 200 && response.response) {
          setPaymentMethodOptions(response.response);
        } else {
          toaster.addToast('Error loading payment methods', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        toaster.addToast('Error loading payment methods', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    void loadPaymentMethods();
    // We want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch payments from API
  const fetchPayments = useCallback(
    async (memberId: string): Promise<void> => {
      if (!memberId || memberId === 'new') {
        setPayments([]);
        return;
      }

      setLoadingPayments(true);

      try {
        const endpoint = `/members/${memberId}/payments`;
        const apiResponse = await api.get<IClubMemberPaymentDto[]>(endpoint);

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
          // Process dates in payments data
          // Convert UTC string dates to Date objects by appending 'Z' to ensure UTC parsing
          const processedPayments = apiResponse.response.map((payment) => {
            // Helper to safely convert UTC strings to Date objects
            const parseUtcDate = (dateValue: any): Date => {
              if (!dateValue) return new Date();
              if (dateValue instanceof Date) return dateValue;

              // If string, ensure it's parsed as UTC
              if (typeof dateValue === 'string') {
                let dateStr = dateValue.trim().replace(' ', 'T');
                if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
                  dateStr += 'Z';
                }
                return new Date(dateStr);
              }

              return new Date(dateValue);
            };

            return {
              ...payment,
              paymentDateUtc: parseUtcDate(payment.paymentDateUtc),
              dateCreatedUtc: parseUtcDate(payment.dateCreatedUtc),
              lastModifiedDateUtc: payment.lastModifiedDateUtc
                ? parseUtcDate(payment.lastModifiedDateUtc)
                : null
            };
          });

          setPayments(processedPayments);
        } else {
          setPayments([]);
          toaster.addToast('Error fetching payments', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        setPayments([]);
        toaster.addToast('Error fetching payments', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingPayments(false);
      }
    },
    [api, toaster]
  );

  // Load payments when club member ID changes
  useEffect(() => {
    if (clubMemberId && clubMemberId !== 'new') {
      void fetchPayments(clubMemberId);
    } else {
      setPayments([]);
    }
    // fetchPayments is stable but we only want this to run when clubMemberId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubMemberId]);

  const updatePaymentFormData = (field: string, value: string) => {
    if (savingPayments) return;

    setPaymentFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear related field errors when user updates data
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith(`payments:${field}`)));
  };

  const clearPaymentForm = () => {
    setPaymentFormData({
      paymentId: '',
      paymentDateUtc: Helpers.toDateString(new Date()),
      billingYear: getCurrentBillingYear().toString(),
      paymentMethodId: '',
      amount: '',
      notes: ''
    });
    setPaymentFormMode('disabled');
    setEditingPaymentId(null);
    setSelectedPaymentRow(null);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('payments:')));
  };

  const enableAddPaymentMode = () => {
    clearPaymentForm();
    setPaymentFormMode('add');
  };

  const enableEditPaymentMode = (paymentItem: IClubMemberPaymentDto, rowIndex: number) => {
    // Populate form with existing data
    setPaymentFormData({
      paymentId: paymentItem.id.toString(),
      paymentDateUtc: Helpers.toDateStringFromUtc(paymentItem.paymentDateUtc),
      billingYear: paymentItem.billingYear.toString(),
      paymentMethodId: paymentItem.paymentMethodId.toString(),
      amount: paymentItem.amount.toString(),
      notes: paymentItem.notes
    });
    setPaymentFormMode('edit');
    setEditingPaymentId(paymentItem.id);
    setSelectedPaymentRow(rowIndex);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('payments:')));
  };

  const validatePaymentForm = (): boolean => {
    const errors: string[] = [];

    if (Helpers.isNullOrWhiteSpace(paymentFormData.paymentDateUtc)) {
      errors.push('payments:paymentDateUtc');
    }

    if (Helpers.isNullOrWhiteSpace(paymentFormData.billingYear)) {
      errors.push('payments:billingYear');
    } else {
      const yearValue = parseInt(paymentFormData.billingYear);
      if (isNaN(yearValue) || yearValue < 1900 || yearValue > 2100) {
        errors.push('payments:billingYear');
      }
    }

    if (Helpers.isNullOrWhiteSpace(paymentFormData.paymentMethodId)) {
      errors.push('payments:paymentMethodId');
    }

    const amountValue = parseFloat(paymentFormData.amount);
    if (isNaN(amountValue) || amountValue < 0) {
      errors.push('payments:amount');
    }

    // Add errors to existing field errors
    setFieldErrors((prev) => [
      ...prev.filter((error) => !error.startsWith('payments:')),
      ...errors
    ]);
    return errors.length === 0;
  };

  const savePayment = async (): Promise<void> => {
    if (savingPayments || !clubMemberId || clubMemberId === 'new') return;

    const isValid = validatePaymentForm();
    if (!isValid) {
      toaster.addToast('Please fill in all required fields correctly', ToastStyle.ERROR, 3000);
      return;
    }

    setSavingPayments(true);

    try {
      if (paymentFormMode === 'add') {
        // Create new payment entry
        const endpoint = `/members/${clubMemberId}/payments`;
        const paymentData: IClubMemberPaymentAddRequest = {
          clubMemberId: parseInt(clubMemberId),
          billingYear: parseInt(paymentFormData.billingYear),
          paymentMethodId: parseInt(paymentFormData.paymentMethodId),
          amount: parseFloat(paymentFormData.amount),
          paymentDateUtc: new Date(paymentFormData.paymentDateUtc + 'T12:00:00.000Z'),
          notes: paymentFormData.notes
        };

        const apiResponse = await api.post<IClubMemberPaymentResult>(
          endpoint,
          JSON.stringify(paymentData)
        );

        if (apiResponse?.status?.statusCode === 200 || apiResponse?.status?.statusCode === 201) {
          toaster.addToast('Payment added successfully', ToastStyle.SUCCESS, 3000);
          await fetchPayments(clubMemberId);
          clearPaymentForm();
        } else {
          toaster.addToast(
            `Error adding payment: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      } else if (paymentFormMode === 'edit') {
        // Update existing payment entry
        const endpoint = `/members/${clubMemberId}/payments/${editingPaymentId}`;

        // Get the original payment to preserve server-managed fields
        const originalPayment = payments.find((p) => p.id === editingPaymentId);
        if (!originalPayment) {
          toaster.addToast('Error: Original payment not found', ToastStyle.ERROR, 3000);
          setSavingPayments(false);
          return;
        }

        const paymentData: IClubMemberPaymentDto = {
          ...originalPayment,
          billingYear: parseInt(paymentFormData.billingYear),
          paymentMethodId: parseInt(paymentFormData.paymentMethodId),
          amount: parseFloat(paymentFormData.amount),
          paymentDateUtc: new Date(paymentFormData.paymentDateUtc + 'T12:00:00.000Z'),
          notes: paymentFormData.notes
        };

        const apiResponse = await api.put<IClubMemberPaymentResult>(
          endpoint,
          JSON.stringify(paymentData)
        );

        if (apiResponse?.status?.statusCode === 200) {
          toaster.addToast('Payment updated successfully', ToastStyle.SUCCESS, 3000);
          await fetchPayments(clubMemberId);
          clearPaymentForm();
        } else {
          toaster.addToast(
            `Error updating payment: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      }
    } catch (error) {
      toaster.addToast(
        `Error ${paymentFormMode === 'add' ? 'adding' : 'updating'} payment`,
        ToastStyle.ERROR,
        5000
      );
    } finally {
      setSavingPayments(false);
    }
  };

  // Get available years from payments for filter dropdown
  const getPaymentAvailableYears = (): string[] => {
    const years = payments
      .map((payment) => payment.billingYear.toString())
      .filter((year, index, arr) => arr.indexOf(year) === index)
      .sort((a, b) => parseInt(b) - parseInt(a)); // Sort descending
    return ['All', ...years];
  };

  // Get payment summary for current and previous billing year
  const getPaymentSummary = (invoices: IAnnualInvoiceDto[] = []) => {
    const currentBillingYear = getCurrentBillingYear();
    const lastBillingYear = currentBillingYear - 1;

    const calculateYearSummary = (year: number) => {
      // Calculate invoice amount from invoice data
      const yearInvoices = invoices.filter((invoice) => invoice.billingYear === year);
      const invoiceAmount = yearInvoices.reduce((sum, invoice) => {
        return sum + invoice.dues + invoice.lateFee + invoice.initiationFee + invoice.otherFee;
      }, 0);

      // Calculate paid amount from payment data - only count Completed payments
      const yearPayments = payments.filter(
        (payment) =>
          payment.billingYear === year && payment.paymentStatus === PaymentStatus.Completed
      );
      const paidAmount = yearPayments.reduce((sum, payment) => sum + payment.amount, 0);

      return { year, invoiceAmount, paidAmount };
    };

    return [calculateYearSummary(currentBillingYear), calculateYearSummary(lastBillingYear)];
  };

  // Handle payment sorting
  const handlePaymentSort = (sortField: string) => {
    if (paymentSortField === sortField) {
      // Toggle sort direction if same field
      setPaymentSortDirection(
        paymentSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      // New field, start with ascending
      setPaymentSortField(sortField);
      setPaymentSortDirection(SortDirection.ASCENDING);
    }
  };

  // Get filtered and sorted payments for display
  const getFilteredAndSortedPayments = (): IClubMemberPaymentDto[] => {
    // Only show Completed and Refunded payments (exclude Pending, Failed, Cancelled)
    let filtered = payments.filter(
      (payment) =>
        payment.paymentStatus === PaymentStatus.Completed ||
        payment.paymentStatus === PaymentStatus.Refunded
    );

    // Apply year filter
    if (paymentYearFilter !== 'All') {
      const filterYear = parseInt(paymentYearFilter);
      filtered = filtered.filter((payment) => payment.billingYear === filterYear);
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (paymentSortField) {
        case 'paymentStatus':
          aValue = a.paymentStatus;
          bValue = b.paymentStatus;
          break;
        case 'paymentDateUtc':
          // Date objects - get timestamps for comparison
          aValue = a.paymentDateUtc.getTime();
          bValue = b.paymentDateUtc.getTime();
          break;
        case 'billingYear':
          aValue = a.billingYear;
          bValue = b.billingYear;
          break;
        case 'paymentMethodId':
          aValue = a.paymentMethodId;
          bValue = b.paymentMethodId;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'orderNumber':
          aValue = a.orderNumber || '';
          bValue = b.orderNumber || '';
          break;
        case 'notes':
          aValue = a.notes.toLowerCase();
          bValue = b.notes.toLowerCase();
          break;
        case 'createdBy':
          aValue = a.createdBy.toLowerCase();
          bValue = b.createdBy.toLowerCase();
          break;
        case 'dateCreatedUtc':
          // Date objects - get timestamps for comparison
          aValue = a.dateCreatedUtc.getTime();
          bValue = b.dateCreatedUtc.getTime();
          break;
        case 'lastModifiedDateUtc':
          // Date objects (or null) - get timestamps for comparison
          aValue = a.lastModifiedDateUtc ? a.lastModifiedDateUtc.getTime() : 0;
          bValue = b.lastModifiedDateUtc ? b.lastModifiedDateUtc.getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return paymentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return paymentSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });
  };

  return {
    // Payment data
    payments,
    loadingPayments,
    savingPayments,

    // Payment form
    paymentFormData,
    paymentFormMode,
    selectedPaymentRow,
    editingPaymentId,

    // Payment method options
    paymentMethodOptions,
    loadingPaymentMethods,

    // Filtering and sorting
    paymentYearFilter,
    paymentSortField,
    paymentSortDirection,

    // Form actions
    updatePaymentFormData,
    clearPaymentForm,
    enableAddPaymentMode,
    enableEditPaymentMode,
    savePayment,

    // Utility functions
    setPaymentYearFilter,
    handlePaymentSort,
    getPaymentAvailableYears,
    getFilteredAndSortedPayments,
    getPaymentSummary,
    clubMemberId,
    fieldErrors
  };
};
